import type { ReactNode } from 'react'
import { forwardRef, useImperativeHandle, useState } from 'react'
import { Loader } from '@systemeio/components'
import type { SuggestionProps } from '@tiptap/suggestion'
import { cn } from 'cn'
import { useTranslation } from 'next-i18next/pages'
import type { Suggestion } from 'shared/components/suggest-mention/types'

export interface MentionSuggestionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean
}

export const MentionSuggestionList = forwardRef<
  MentionSuggestionListRef,
  SuggestionProps<Suggestion>
>(function MentionSuggestionList({ items, command, loading }, ref): ReactNode {
  const { t } = useTranslation()
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const index = Math.max(
    0,
    items.findIndex(item => item.userId === selectedId),
  )

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (items.length === 0) return false
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        const delta = event.key === 'ArrowUp' ? -1 : 1
        const next = (index + delta + items.length) % items.length
        setSelectedId(items[next]?.userId ?? null)
        return true
      }
      if (event.key !== 'Enter') return false
      const item = items[index]
      if (!item) return false
      command({ id: item.userId, label: item.displayName, profileImageUrl: item.profileImageUrl })
      return true
    },
  }))

  if (loading && items.length === 0) {
    return (
      <div className="max-h-[250px] w-max max-w-[420px] min-w-[260px] overflow-y-auto rounded-md bg-bg-primary py-1 shadow-2">
        <div className="flex items-center justify-center py-3.5">
          <Loader />
        </div>
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className="max-h-[250px] w-max max-w-[420px] min-w-[260px] overflow-y-auto rounded-md bg-bg-primary py-1 shadow-2">
        <div className="px-4 py-2 text-sm text-text-secondary">
          {t('community.editor.mentions.empty')}
        </div>
      </div>
    )
  }

  return (
    <div className="max-h-[250px] w-max max-w-[420px] min-w-[260px] overflow-y-auto rounded-md bg-bg-primary py-1 shadow-2">
      {items.map((item, i) => (
        <button
          key={item.userId}
          type="button"
          title={item.displayName}
          className={cn(
            'flex min-h-8 w-full min-w-0 cursor-pointer items-center justify-start gap-2 px-4 text-left',
            i === index
              ? 'bg-bg-accent-active text-text-on'
              : 'text-text-secondary hover:bg-bg-accent-active/10',
          )}
          onMouseDown={event => {
            event.preventDefault()
            event.stopPropagation()
            command({
              id: item.userId,
              label: item.displayName,
              profileImageUrl: item.profileImageUrl,
            })
          }}
          onMouseEnter={() => {
            setSelectedId(item.userId)
          }}
        >
          {item.profileImageUrl ? (
            <img
              className="size-5 shrink-0 rounded-full"
              width={20}
              height={20}
              src={item.profileImageUrl}
              alt={`Avatar of ${item.displayName}`}
            />
          ) : null}
          <strong className="line-clamp-2 min-w-0 flex-1 text-[15px] wrap-break-word">
            {item.displayName}
          </strong>
        </button>
      ))}
    </div>
  )
})
